import { Eye, EyeOff } from 'lucide-react';
import React, { useContext, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router';

import { MfaRequiredError } from '@/apiClients/auth';
import { Capabilities } from '@/auth-capabilities';
import { Button, cn, IconButton, Input } from '@/components/ui';
import { auditorMediaQueries, config } from '@/config';
import { AppCtx } from '@/context/appContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import AuthWrapper from '@/sections/auth/AuthWrapper';
import { safeNextPath } from '@/utils/safeNextPath';

// The email pattern the form has always used (yup 0.32's `string().email()`), kept so the
// login form accepts and rejects exactly the same addresses as before.
export const EMAIL_PATTERN =
  /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;

type Credentials = { email: string; password: string };
type CredentialErrors = Partial<Record<keyof Credentials, string>>;

const MAX_LENGTH = 255;

/** Same rules and messages as the old yup schema, first failing rule per field. */
export const validateCredentials = ({ email, password }: Credentials): CredentialErrors => {
  const errors: CredentialErrors = {};
  if (!email) {
    errors.email = 'Email is required';
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = 'Must be a valid email';
  } else if (email.length > MAX_LENGTH) {
    errors.email = `email must be at most ${MAX_LENGTH} characters`;
  }
  if (!password) {
    errors.password = 'Password is required';
  } else if (password.length > MAX_LENGTH) {
    errors.password = `password must be at most ${MAX_LENGTH} characters`;
  }
  return errors;
};

export const Login = () => {
  // tip. using inferred typing
  const { authInfo, hasAuth } = useContext(AppCtx);
  const [searchParams] = useSearchParams();
  const defaultUrl = !!authInfo && hasAuth(Capabilities.mpqItemView) ? '/' : '/foods';
  const next = safeNextPath(searchParams.get('next'), defaultUrl);

  useEffect(() => {}, []);

  return authInfo ? (
    <Navigate to={next} replace />
  ) : (
    <AuthWrapper>
      <div className="col-span-full grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <h3 className="m-0 mb-1 text-2xl font-semibold tracking-title">Login</h3>
        </div>
        <div className="col-span-12">
          <AuthLogin />
        </div>
      </div>
    </AuthWrapper>
  );
};

type PendingMfa = { email: string; password: string };

const AuthLogin = () => {
  const { authRes } = useContext(AppCtx);
  // Set only when the server answers ``mfa_token_required`` — accounts without
  // MFA never leave the credentials step. Held in memory only (never storage)
  // because the OTP step must re-send the password alongside the code.
  const [pendingMfa, setPendingMfa] = React.useState<PendingMfa | null>(null);
  const submittedRef = React.useRef<PendingMfa | null>(null);

  useEffect(() => {
    if (authRes.isError && authRes.error instanceof MfaRequiredError && submittedRef.current) {
      setPendingMfa(submittedRef.current);
    }
  }, [authRes]);

  return pendingMfa ? (
    <MfaStep pending={pendingMfa} onBack={() => setPendingMfa(null)} />
  ) : (
    <CredentialsStep
      onSubmitted={creds => {
        submittedRef.current = creds;
      }}
    />
  );
};

const CredentialsStep = ({ onSubmitted }: { onSubmitted: (creds: PendingMfa) => void }) => {
  const [capsWarning, setCapsWarning] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const { login, authRes } = useContext(AppCtx);
  const [values, setValues] = React.useState<Credentials>({ email: '', password: '' });
  const [touched, setTouched] = React.useState<Partial<Record<keyof Credentials, boolean>>>({});
  const errors = validateCredentials(values);
  const isMfaPrompt = authRes.isError && authRes.error instanceof MfaRequiredError;
  const submitError = authRes.isError && !isMfaPrompt ? authErrorMessage(authRes.error) : null;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues(current => ({ ...current, [name]: value }));
  };
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setTouched(current => ({ ...current, [name]: true }));
  };
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ email: true, password: true });
    if (Object.keys(errors).length > 0) {
      return;
    }
    onSubmitted(values);
    login(values);
  };
  const fieldError = (field: keyof Credentials) => (touched[field] ? errors[field] : undefined);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.SyntheticEvent) => {
    event.preventDefault();
  };

  const onKeyDown = (keyEvent: any) => {
    if (keyEvent.getModifierState('CapsLock')) {
      setCapsWarning(true);
    } else {
      setCapsWarning(false);
    }
  };

  return (
    <form noValidate onSubmit={onSubmit}>
      <div className="col-span-full grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <div className="flex flex-col gap-2">
            <label className="m-0 text-xs font-medium text-ink-muted" htmlFor="email-login">
              Email Address
            </label>
            <Input
              id="email-login"
              type="email"
              value={values.email}
              name="email"
              onBlur={onBlur}
              onChange={onChange}
              invalid={Boolean(fieldError('email'))}
            />
            {fieldError('email') && (
              <p className="m-0 text-xs text-ink-muted" id="standard-weight-helper-text-email-login">
                {fieldError('email')}
              </p>
            )}
          </div>
        </div>
        <div className="col-span-12">
          <div className="flex flex-col gap-2">
            <label className="m-0 text-xs font-medium text-ink-muted" htmlFor="password-login">
              Password
            </label>
            <div className="relative">
              <Input
                className={cn('pr-10', capsWarning && 'border-caution')}
                invalid={Boolean(fieldError('password'))}
                id="-password-login"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                name="password"
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  setCapsWarning(false);
                  onBlur(event);
                }}
                onKeyDown={onKeyDown}
                onChange={onChange}
              />
              <IconButton
                label={showPassword ? 'Hide password' : 'Show password'}
                variant="ghost"
                size="small"
                className="absolute right-1 top-1"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? (
                  <Eye aria-hidden="true" className="size-4" />
                ) : (
                  <EyeOff aria-hidden="true" className="size-4" />
                )}
              </IconButton>
            </div>
            {capsWarning && (
              <span className="m-0 text-xs text-caution-ink" id="warning-helper-text-password-login">
                Caps lock on!
              </span>
            )}
            {fieldError('password') && (
              <p className="m-0 text-xs text-ink-muted" id="standard-weight-helper-text-password-login">
                {fieldError('password')}
              </p>
            )}
          </div>
        </div>

        {submitError && (
          <div className="col-span-12">
            <p className="m-0 text-xs text-ink-muted">{submitError}</p>
          </div>
        )}
        <div className="col-span-12">
          <Button className="w-full" disabled={authRes.isPending} size="large" type="submit">
            Login
          </Button>
        </div>
        <div className="col-span-12">
          <span className="m-0 text-xs text-ink-muted">{config.RELEASE_LABEL}</span>
        </div>
      </div>
    </form>
  );
};

const authErrorMessage = (error: unknown) => (error instanceof Error ? error.message : '' + error);

const MfaStep = ({ pending, onBack }: { pending: PendingMfa; onBack: () => void }) => {
  const phone = useMediaQuery(auditorMediaQueries.downSm);
  const { login, authRes } = useContext(AppCtx);
  const [code, setCode] = React.useState('');
  const [resent, setResent] = React.useState(false);

  const busy = authRes.isPending;
  // A fresh MfaRequiredError just means a new code was emailed (resend).
  const error =
    authRes.isError && !(authRes.error instanceof MfaRequiredError) ? authErrorMessage(authRes.error) : null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setResent(false);
    login({ ...pending, otp: code });
  };

  const onResend = () => {
    setCode('');
    setResent(true);
    // Re-submitting without a code makes the server email a new one.
    login(pending);
  };

  return (
    <form noValidate onSubmit={onSubmit}>
      <div className="col-span-full grid grid-cols-12 gap-6">
        <div className="col-span-12">
          <h5 className="m-0 text-base font-semibold">Enter verification code</h5>
          <p className="m-0 mt-2 text-xs text-ink-muted">
            {resent
              ? `We emailed a new 6-digit code to ${pending.email}.`
              : `We emailed a 6-digit code to ${pending.email}. Enter it below to finish signing in.`}
          </p>
        </div>
        <div className="col-span-12">
          <div className="flex flex-col gap-2">
            <label className="m-0 text-xs font-medium text-ink-muted" htmlFor="otp-login">
              Verification code
            </label>
            <Input
              id="otp-login"
              name="otp"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus={!phone}
              invalid={!!error}
            />
          </div>
        </div>
        {error && (
          <div className="col-span-12">
            <p className="m-0 text-xs text-ink-muted">{error}</p>
          </div>
        )}
        <div className="col-span-12">
          <Button className="w-full" disabled={busy || code.length < 6} size="large" type="submit">
            Verify
          </Button>
        </div>
        <div className="col-span-12">
          <div className="flex justify-between">
            <Button variant="ghost" onClick={onBack} disabled={busy}>
              Back to sign in
            </Button>
            <Button variant="ghost" onClick={onResend} disabled={busy}>
              Resend code
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
